import * as styles from "./MonthPickerTrigger.css";

import { clsx } from "clsx";
import type { KeyboardEvent, PointerEvent } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import type { BaseUIEvent } from "@base-ui/react/types";
import { Icon } from "@frontend/components/atoms/Icon";
import { pointerOffsetIn } from "@frontend/utils/pointerAlign";

import { useMonthPickerContext } from "./MonthPicker.context";
import type { MonthPickerTriggerProps } from "./MonthPicker.types";

export function MonthPickerTrigger({
  className,
  children,
  onPointerDown,
  onKeyDown,
  ...props
}: MonthPickerTriggerProps) {
  const { size, layout, disabled, pointerOffsetRef } = useMonthPickerContext();

  const isText = layout === "text";

  function handlePointerDown(
    event: BaseUIEvent<PointerEvent<HTMLButtonElement>>,
  ) {
    pointerOffsetRef.current = pointerOffsetIn(
      event.currentTarget,
      event.clientX,
    );

    onPointerDown?.(event);
  }

  function handleKeyDown(event: BaseUIEvent<KeyboardEvent<HTMLButtonElement>>) {
    if (event.key === "Enter" || event.key === " ") {
      // Opened via keyboard, no click position to open under: center it.
      pointerOffsetRef.current = 0;
    }

    onKeyDown?.(event);
  }

  return (
    <BasePopover.Trigger
      data-slot="month-picker-trigger"
      disabled={disabled}
      className={clsx(
        isText ? styles.textTrigger : styles.trigger({ size }),
        className,
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
      <span className={isText ? styles.textIcon : styles.icon}>
        <Icon name="CalendarRange" />
      </span>
    </BasePopover.Trigger>
  );
}
